import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Settings2 } from 'lucide-react'
import type { Canon, Profile } from '../../core/types.ts'
import { evaluate } from '../../core/rules.ts'
import { api, type ListSummary } from '../lib/api.ts'
import { Link } from '../lib/router.tsx'
import { Shell } from '../components/Shell.tsx'
import { WorkTable } from '../components/WorkTable.tsx'
import { RulePanel } from '../components/RulePanel.tsx'
import { LockBanner } from '../components/LockBanner.tsx'
import { ListSettingsDialog } from '../components/ListSettingsDialog.tsx'
import { Alert } from '../components/ui/alert.tsx'
import { Button } from '../components/ui/button.tsx'
import type { Theme } from '../lib/theme.ts'

/**
 * Editor. Pravidla se počítají v prohlížeči stejnou funkcí jako na serveru,
 * takže zpětná vazba je okamžitá při každém kliknutí a nikde se nerozejde.
 */
export function ListEditorPage({ listId, theme, onToggleTheme }: { listId: string; theme: Theme; onToggleTheme: () => void }) {
  const [list, setList] = useState<ListSummary | null>(null)
  const [canon, setCanon] = useState<Canon | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async () => {
    try {
      const l = await api.list(listId)
      setList(l)
      setSelected(new Set(l.selectedWorkIds))
      const [c, profiles] = await Promise.all([api.canon(l.canonId), api.profiles()])
      setCanon(c)
      setProfile(profiles.find((p) => p.id === l.profileId) ?? profiles[0] ?? null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seznam se nepodařilo načíst')
    }
  }, [listId])

  useEffect(() => void load(), [load])

  const locked = list ? list.hasPassword && !list.canEdit : false

  const evaluation = useMemo(
    () => (canon && profile ? evaluate(profile, canon.works, [...selected]) : null),
    [canon, profile, selected],
  )

  /** Optimistický zápis: UI reaguje hned, uložení se sdruží do jednoho PUT. */
  const toggle = useCallback(
    (workId: string) => {
      if (locked) return
      setSelected((prev) => {
        const next = new Set(prev)
        next.has(workId) ? next.delete(workId) : next.add(workId)

        if (saveTimer.current) clearTimeout(saveTimer.current)
        saveTimer.current = setTimeout(() => {
          api.updateList(listId, { selectedWorkIds: [...next] }).catch((err) => {
            setError(err instanceof Error ? err.message : 'Uložení se nepodařilo')
            void load() // vrátíme se k tomu, co skutečně leží na serveru
          })
        }, 400)

        return next
      })
    },
    [locked, listId, load],
  )

  if (error && !list) {
    return (
      <Shell theme={theme} onToggleTheme={onToggleTheme} title="Seznam se nepodařilo načíst">
        <Alert variant="destructive">{error}</Alert>
        <Button asChild variant="outline" className="mt-4"><Link to="/">Zpět na přehled</Link></Button>
      </Shell>
    )
  }

  if (!list || !canon || !profile || !evaluation) {
    return <Shell theme={theme} onToggleTheme={onToggleTheme} title="Načítám…"><div /></Shell>
  }

  return (
    <Shell
      theme={theme}
      onToggleTheme={onToggleTheme}
      title={list.name}
      subtitle={<>{list.studentName ? `${list.studentName} · ` : ''}{canon.name}</>}
      actions={
        <>
          <Button asChild variant="ghost" size="sm"><Link to="/"><ArrowLeft size={15} aria-hidden /> Přehled</Link></Button>
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
            <Settings2 size={15} aria-hidden /> Nastavení
          </Button>
        </>
      }
    >
      {locked && <div className="mb-4"><LockBanner listId={listId} onUnlocked={load} /></div>}
      {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}

      <div data-locked={locked || undefined} className="flex flex-col gap-[var(--space-6)] editor:flex-row">
        <WorkTable
          works={canon.works}
          profile={profile}
          evaluation={evaluation}
          selected={selected}
          locked={locked}
          onToggle={toggle}
        />
        <RulePanel
          evaluation={evaluation}
          works={canon.works}
          total={20}
          locked={locked}
          listId={listId}
          onRemove={toggle}
        />
      </div>

      <ListSettingsDialog list={list} open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={load} />
    </Shell>
  )
}
